import { useEffect, useRef } from 'react';
import type { ForwardedRef } from 'react';
import type { CopilotKitMessage } from './types';
import { isUserMessage } from './types';

type UseAutoScrollOptions = {
  messages: CopilotKitMessage[];
  containerRef: ForwardedRef<HTMLDivElement>;
  threshold?: number;
};

/**
 * Custom hook for auto-scrolling chat messages to bottom
 *
 * Behavior:
 * - Always scrolls to bottom when user sends a message
 * - Auto-scrolls when new messages arrive if user is near bottom (within threshold)
 * - Doesn't scroll if user has scrolled up away from bottom
 * - Re-enables auto-scroll when user scrolls back near bottom
 *
 * @param messages - Array of chat messages
 * @param containerRef - Ref to the scrollable container
 * @param threshold - Distance from bottom (in px) to consider "at bottom" (default: 100)
 */
export const useAutoScroll = ({
  messages,
  containerRef,
  threshold = 100,
}: UseAutoScrollOptions) => {
  const shouldAutoScrollRef = useRef(true);
  const lastMessageCountRef = useRef(messages.length);
  const lastMessagesRef = useRef<CopilotKitMessage[]>(messages);
  const lastScrollTopRef = useRef<number>(0);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const container =
      containerRef &&
      typeof containerRef === 'object' &&
      'current' in containerRef
        ? containerRef.current
        : null;

    if (!container) return;

    // Check if the last message is a new user message
    const lastMessage = messages[messages.length - 1];
    const prevLastMessage =
      lastMessagesRef.current[lastMessagesRef.current.length - 1];
    const isNewUserMessage =
      lastMessage &&
      isUserMessage(lastMessage) &&
      lastMessage.id !== prevLastMessage?.id;

    // Always re-enable auto-scroll for new user messages
    if (isNewUserMessage) {
      shouldAutoScrollRef.current = true;
    }

    // Auto-scroll if:
    // 1. New user message (always scroll to show user's own message), OR
    // 2. Auto-scroll is enabled AND messages changed
    const shouldScroll =
      isNewUserMessage ||
      (shouldAutoScrollRef.current &&
        messages.length >= lastMessageCountRef.current);

    if (shouldScroll) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        // Re-check intent: user may have scrolled up in the ~16ms window
        if (container && shouldAutoScrollRef.current) {
          container.scrollTop = container.scrollHeight;
          // Read back the actual (clamped) scrollTop, not scrollHeight
          lastScrollTopRef.current = container.scrollTop;
        }
      });
    }

    lastMessageCountRef.current = messages.length;
    lastMessagesRef.current = messages;
  }, [messages, containerRef, threshold]);

  // Track scroll position to re-enable auto-scroll when user scrolls back to bottom
  useEffect(() => {
    const container =
      containerRef &&
      typeof containerRef === 'object' &&
      'current' in containerRef
        ? containerRef.current
        : null;

    if (!container) return;

    // Initialize tracker to current position
    lastScrollTopRef.current = container.scrollTop;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom < threshold;
      const isScrollingUp = scrollTop < lastScrollTopRef.current;

      lastScrollTopRef.current = scrollTop;

      if (isScrollingUp && !isNearBottom) {
        // Guard against layout-reflow scroll events (e.g. modal opening) disabling auto-scroll.
        shouldAutoScrollRef.current = false;
      } else if (isNearBottom) {
        // Scrolling down and near bottom re-attaches
        shouldAutoScrollRef.current = true;
      }
    };

    // Update lastScrollTopRef on resize so the clamp-triggered scroll event
    // isn't misread as an upward user scroll
    const resizeObserver = new ResizeObserver(() => {
      lastScrollTopRef.current = container.scrollTop;
    });

    resizeObserver.observe(container);
    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [containerRef, threshold]);
};
